/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { RuleContext } from '@alfresco/adf-extensions';

/**
 * Types MIME qu'Alfresco sait rendre en PDF.
 *
 * CE QUE CETTE LISTE REMPLACE. Il y avait ici une copie mot pour mot de la carte
 * `supportedExtensions` d'ACA (`projects/aca-shared/rules/src/app.rules.ts`), qui associe une
 * extension au PROGRAMME OFFICE capable de l'ouvrir — `doc: 'ms-word'`, `xls: 'ms-excel'`... Elle
 * sert a l'action « Modifier dans Office », et elle ne dit rien de la convertibilite en PDF. Or
 * `canConvertToPDF` s'en servait comme critere.
 *
 * Le resultat etait faux dans les deux sens :
 *
 * - LE FORMAT LIBREOFFICE ETAIT REFUSE. Ni `odt`, ni `ods`, ni `odp`, ni `odg` n'y figurent, alors
 *   qu'Alfresco les convertit tous. L'action n'apparaissait donc pas sur un document ODF ;
 * - le csv, le txt, le html, le tsv, le tiff, les formats StarOffice et WordPerfect etaient
 *   refuses pour la meme raison ;
 * - a l'inverse, `vst`, `vssx`, `vssm`, `vstx`, `vstm`, `xlt`, `xlam`, `pot`, `pps` y figuraient
 *   sans qu'aucun transformateur ne les prenne : l'action s'affichait et la rendition ne venait
 *   jamais.
 *
 * D'OU VIENT CELLE-CI. Du moteur de transformation lui-meme : les couples source/cible qu'il
 * declare sur `/transform/config`, filtres sur `application/pdf`. C'est exactement ce que le
 * repository interroge pour savoir s'il peut produire la rendition.
 */
export const pdfConvertibleMimeTypes: ReadonlySet<string> = new Set([
  'application/dita+xml',
  'application/msword',
  'application/rtf',
  'application/vnd.ms-excel',
  'application/vnd.ms-excel.sheet.binary.macroenabled.12',
  'application/vnd.ms-excel.sheet.macroenabled.12',
  'application/vnd.ms-excel.template.macroenabled.12',
  'application/vnd.ms-powerpoint',
  'application/vnd.ms-powerpoint.addin.macroenabled.12',
  'application/vnd.ms-powerpoint.presentation.macroenabled.12',
  'application/vnd.ms-powerpoint.slide.macroenabled.12',
  'application/vnd.ms-powerpoint.slideshow.macroenabled.12',
  'application/vnd.ms-powerpoint.template.macroenabled.12',
  'application/vnd.ms-word.document.macroenabled.12',
  'application/vnd.ms-word.template.macroenabled.12',
  'application/vnd.oasis.opendocument.graphics',
  'application/vnd.oasis.opendocument.presentation',
  'application/vnd.oasis.opendocument.presentation-template',
  'application/vnd.oasis.opendocument.spreadsheet',
  'application/vnd.oasis.opendocument.spreadsheet-template',
  'application/vnd.oasis.opendocument.text',
  'application/vnd.oasis.opendocument.text-template',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.openxmlformats-officedocument.presentationml.slide',
  'application/vnd.openxmlformats-officedocument.presentationml.slideshow',
  'application/vnd.openxmlformats-officedocument.presentationml.template',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.template',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.template',
  'application/vnd.sun.xml.calc',
  'application/vnd.sun.xml.calc.template',
  'application/vnd.sun.xml.impress',
  'application/vnd.sun.xml.impress.template',
  'application/vnd.sun.xml.writer',
  'application/vnd.sun.xml.writer.template',
  'application/vnd.visio',
  'application/vnd.visio2013',
  'application/wordperfect',
  'image/tiff',
  'text/csv',
  'text/html',
  'text/plain',
  'text/tab-separated-values',
  'text/xml'
]);

/**
 * Les memes formats, par extension.
 *
 * Sert uniquement de repli pour les RACCOURCIS : un `app:filelink` ne porte pas de propriete
 * `content`, donc pas de type MIME — seul le nom du fichier vise renseigne sur son format.
 *
 * Correspondance tiree de `alfresco/mimetype/jodconverter-document-formats.json`, embarque dans
 * `alfresco-repository.jar` : la table que le convertisseur LibreOffice emploie. Les quinze types
 * qu'elle ne couvre pas — OOXML a macros, rtf, tiff, xml, dita — sont completes par leurs
 * extensions officielles.
 */
export const pdfConvertibleExtensions: ReadonlySet<string> = new Set([
  'csv',
  'dita',
  'doc',
  'docm',
  'docx',
  'dotm',
  'dotx',
  'html',
  'odg',
  'odp',
  'ods',
  'odt',
  'otp',
  'ots',
  'ott',
  'potm',
  'potx',
  'ppam',
  'ppsm',
  'ppsx',
  'ppt',
  'pptm',
  'pptx',
  'rtf',
  'sldm',
  'sldx',
  'stc',
  'sti',
  'stw',
  'sxc',
  'sxi',
  'sxw',
  'tif',
  'tiff',
  'tsv',
  'txt',
  'vsd',
  'vsdx',
  'wpd',
  'xls',
  'xlsb',
  'xlsm',
  'xlsx',
  'xltm',
  'xltx',
  'xml'
]);

/** Types de noeud qu'ACA emploie pour ses raccourcis. */
const LINK_NODE_TYPES = ['app:filelink', 'app:folderlink'];

/** Suffixe qu'ACA ajoute au nom d'un raccourci. */
const LINK_SUFFIX = '.url';

/** Le noeud est-il un raccourci ? */
export function isLinkEntry(entry: any): boolean {
  return LINK_NODE_TYPES.includes(entry?.nodeType);
}

/**
 * Nom du fichier vise, derriere un eventuel raccourci.
 *
 * ACA nomme un raccourci `<original>.url`. Le noeud lien ne dit rien du contenu vise — il n'a meme
 * pas de propriete `content` — mais son NOM porte l'extension de l'original. C'est la seule
 * information de type accessible sans interroger la cible, ce qu'un evaluateur, synchrone par
 * nature, ne peut pas faire.
 *
 * Sans cela, un raccourci `rapport.docx.url` donnait l'extension `url`, absente de toutes les
 * listes : aucune action liee au type ne s'affichait sur un lien.
 */
export function effectiveName(entry: any): string {
  const name = entry?.name ?? '';

  return isLinkEntry(entry) && name.toLowerCase().endsWith(LINK_SUFFIX) ? name.slice(0, -LINK_SUFFIX.length) : name;
}

export function getFileExtension(fileName: string): string | null {
  if (fileName) {
    const match = fileName.toLowerCase().match(/\.([^\./\?\#]+)($|\?|\#)/);
    return match ? match[1] : null;
  }

  return null;
}

export function canConvertToPDF(context: RuleContext): boolean {
  if (!context?.selection) {
    return false;
  }
  const { file } = context.selection;
  if (!file?.entry) {
    return false;
  }
  if (isTrashcanSelection(context)) {
    return false;
  }
  // LE TYPE MIME D'ABORD, l'extension seulement en repli.
  //
  // C'est sur le type MIME que le moteur de transformation raisonne ; le deduire d'une extension
  // ne peut qu'introduire un desaccord. `content.mimeType` est renseigne par le repository pour
  // tout fichier, et il est deja la dans la selection.
  const mimeType = file.entry.content?.mimeType;
  if (mimeType) {
    return pdfConvertibleMimeTypes.has(mimeType);
  }

  // Pas de `content` : c'est un raccourci. `effectiveName` donne le nom du fichier vise, dont
  // l'extension est celle de l'original et non `url`.
  const extension = getFileExtension(effectiveName(file.entry));

  return !!extension && pdfConvertibleExtensions.has(extension);
}

/** Les commentaires ne peuvent etre ajoutes qu'a un fichier encore present dans le repository. */
export function canAddComment(context: RuleContext): boolean {
  return !!context?.selection?.file?.entry?.isFile && !isTrashcanSelection(context);
}

/** Detecte la corbeille par la navigation et par les metadonnees de l'entree archivee. */
function isTrashcanSelection(context: RuleContext): boolean {
  const url = context?.navigation?.url ?? '';
  const entry = context?.selection?.file?.entry as any;

  return url.startsWith('/trashcan') || url.includes('#/trashcan') || !!entry?.archivedAt;
}

export function canSendMailTo(context: RuleContext): boolean {
  // Check if the selection is a file
  if (!context?.selection) {
    return false;
  }
  const { file } = context.selection;
  if (!file?.entry) {
    return false;
  }
  return canShareFile(context);
}

export function isShared(context: RuleContext): boolean {
  if (!context?.selection) {
    return false;
  }
  const { file } = context.selection;
  if (!file?.entry) {
    return false;
  }
  return file.entry?.aspectNames?.includes('qshare:shared');
}

export function canEditText(context: RuleContext): boolean {
  // Check if the selection is a file
  if (!context?.selection) {
    return false;
  }
  const { file } = context.selection;
  if (!file?.entry) {
    return false;
  }

  // `content?.` et non `content.` : un raccourci n'a PAS de propriete `content`. La lecture directe
  // levait une TypeError, et cette exception interrompait l'evaluation de TOUTE la barre d'outils —
  // selectionner un `.url` la laissait vide, sans partage, telechargement ni « Plus d'actions ».
  const mimeType = file.entry.content?.mimeType;

  if (mimeType) {
    return mimeType === 'text/plain';
  }

  // Faute de type MIME — cas du raccourci — on se rabat sur l'extension du fichier vise.
  return getFileExtension(effectiveName(file.entry)) === 'txt';
}

export function canRename(context: RuleContext): boolean {
  // Check if the selection is a file
  if (!context?.selection) {
    return false;
  }
  const { file } = context.selection;
  if (!file?.entry) {
    return false;
  }

  if (context.navigation?.url?.startsWith('/trashcan')) {
    return false;
  }

  // Check if get the permission to rename

  return file.entry?.allowableOperations?.includes('update');
}

// Je dois vérifier si c'est bien un fichier et si il n'est pas dans la corbeille ni dans les fichiers personnels
export function canInitiateProcess(context: RuleContext): boolean {
  // Check if the selection is a file
  if (!context?.selection) {
    return false;
  }
  const { file } = context.selection;
  if (!file?.entry) {
    return false;
  }

  if (context.navigation?.url?.startsWith('/trashcan')) {
    return false;
  }

  if (context.navigation?.url?.startsWith('/personal-files')) {
    return false;
  }

  return file.entry.isFile;
}

export function canShareFile(context: RuleContext): boolean {
  // Check if the selection is a file
  if (!context?.selection) {
    return false;
  }
  const { file } = context.selection;
  if (!file?.entry) {
    return false;
  }

  if (context.navigation?.url?.startsWith('/trashcan')) {
    return false;
  }

  return file.entry.isFile;
}

/** Les fonctions sensibles de navigation ne sont visibles que pour un administrateur. */
export function isAdministrator(context: RuleContext): boolean {
  return context?.profile?.isAdmin === true;
}
